import Link from 'next/link';
import Pager from 'rc-pagination';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { CategoryEntity } from '../../../../interfaces/explore/CategoryEntity';
import { MerchantProduct } from '../../../../interfaces/merchants/products';
import Heading from '../../../../atoms/Heading';
import BaseTemplate from '../../../../organisms/merchants/BaseTemplate';
import ProductCard from '../../../../molecules/merchant/common/cards/ProductCard';
import MerchantProfile from '../../../../molecules/merchant/MerchantProfile';
import css from './MerchantProductsPage.module.scss';
import { MerchantDetail } from '../../../../interfaces/merchants';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import { buildSiteUrl } from 'src/utils/request-url';

export interface PageProps {
  pagination: {
    total_size: number;
    current_page: number;
  };
  products: MerchantProduct[];
  merchantDetail: MerchantDetail;
  category: CategoryEntity;
  isCoubicApp: boolean;
}

const MerchantProductsPage: React.FC<PageProps> = ({
  merchantDetail,
  products,
  pagination: { total_size, current_page },
  isCoubicApp,
}) => {
  const handlePageChange = useCallback((page: number) => {
    location.href = `${location.pathname}?page=${page}#pageContent`;
  }, []);
  const intl = useIntl();

  return (
    <>
      <div className={css.MerchantProductsPage}>
        <BaseTemplate merchantDetail={merchantDetail} isCoubicApp={isCoubicApp}>
          <div className={css.MerchantProductsPage__Content}>
            <Heading className={css.MerchantProductsPage__Heading}>
              <Link
                href={buildSiteUrl(`/products#pageContent`, 'next')}
                prefetch={false}
                className={css.MerchantProductsPage__Link}
              >
                {intl.formatMessage({ id: 'merchants.products.index.title' })}
              </Link>
              {current_page > 1 &&
                ` ${intl.formatMessage(
                  { id: 'merchants.products.index.page' },
                  { currentPage: current_page },
                )}`}
            </Heading>
            {products.length > 0 && (
              <div>
                {products.map((product) => (
                  <ProductCard
                    className={css.MerchantProductsPage__ProductCard}
                    thumbnailUrl={
                      product.image && product.image.medium
                        ? product.image.medium.url || ''
                        : ''
                    }
                    name={product.name}
                    description={product.description || ''}
                    scheme={product.scheme}
                    key={product.public_id}
                    url={buildSiteUrl(
                      `/products/${product.public_id}#pageContent`,
                    )}
                    price={product.price}
                    ticketCount={product.ticket_count}
                    ticketExpiration={product.expiration}
                  />
                ))}
              </div>
            )}
            <div className={css.MerchantProductsPage__Pager}>
              <Pager
                onChange={handlePageChange}
                current={current_page}
                total={total_size}
                showTitle={false}
              />
            </div>
            <div className={css.MerchantProductsPage__MerchantProfileSection}>
              <Heading className={css.MerchantProductsPage__Heading}>
                {intl.formatMessage(
                  { id: 'merchants.details.storeData' },
                  { displayName: merchantDetail.display_name },
                )}
              </Heading>
              <MerchantProfile merchantDetail={merchantDetail} />
              <div className={css.MerchantProductsPage__BackToTopButtonSection}>
                <div className="mx-auto max-w-[370px]">
                  <RSButtonAnchor
                    variant="outline"
                    isBlock
                    href={buildSiteUrl()}
                  >
                    {intl.formatMessage({
                      id: 'merchants.details.backToMerchantTop',
                    })}
                  </RSButtonAnchor>
                </div>
              </div>
            </div>
          </div>
        </BaseTemplate>
      </div>

      <style jsx>{`
        :global(.rc-pagination-item:hover) {
          border-color: #0066ff;
        }
        :global(.rc-pagination-item:hover:not(.rc-pagination-item-active) a) {
          color: #0066ff;
        }
        :global(.rc-pagination-item-active) {
          background-color: #0066ff;
          border-color: #0066ff;
        }
        :global(.rc-pagination-jump-prev:hover:after),
        :global(.rc-pagination-jump-next:hover:after) {
          color: #0066ff;
        }
      `}</style>
    </>
  );
};

export default MerchantProductsPage;
